import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ArrowUpRight, Menu, X, MessageCircle } from "lucide-react";
import { NAV_MENUS, LOGO, WA_NUMBER } from "../data/site";

export const slugOf = (label) => label.toLowerCase().replace(/\s/g, "-");

export const scrollTo = (hash) => {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -100, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const goSection = (navigate, hash) => {
  if (window.location.pathname !== "/") {
    navigate("/");
    setTimeout(() => scrollTo(hash), 450);
  } else {
    scrollTo(hash);
  }
};

const panelVariants = {
  hidden: { opacity: 0, y: 10, scale: 0.98, x: "-50%" },
  show: { opacity: 1, y: 0, scale: 1, x: "-50%", transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1], staggerChildren: 0.035 } },
  exit: { opacity: 0, y: 8, scale: 0.98, x: "-50%", transition: { duration: 0.15 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

const Dropdown = ({ menu }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const navigate = useNavigate();
  const slug = slugOf(menu.label);
  const enter = () => { clearTimeout(timer.current); setOpen(true); };
  const leave = () => { timer.current = setTimeout(() => setOpen(false), 140); };
  const pick = (e, item) => {
    setOpen(false);
    if (item.scroll) {
      e.preventDefault();
      goSection(navigate, item.scroll);
    }
  };
  return (
    <div className="relative" onMouseEnter={enter} onMouseLeave={leave}>
      <Link
        to={`/${slug}`}
        data-testid={`nav-${slug}`}
        className="group relative flex items-center gap-1 px-3.5 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors duration-200"
      >
        {menu.label}
        <ChevronDown size={13} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        <span className={`absolute left-3.5 right-3.5 -bottom-0.5 h-px bg-smipl-orange transition-all duration-300 ${open ? "opacity-100" : "opacity-0 scale-x-0 group-hover:opacity-100 group-hover:scale-x-100"} origin-left`} />
      </Link>
      <AnimatePresence>
        {open && (
          <motion.div
            variants={panelVariants}
            initial="hidden"
            animate="show"
            exit="exit"
            data-lenis-prevent
            className={`absolute left-1/2 top-full pt-3 ${menu.wide ? "w-[580px]" : "w-[360px]"} max-w-[92vw]`}
          >
            <div data-lenis-prevent className={`rounded-2xl border border-white/12 bg-[#070C1A]/95 backdrop-blur-2xl p-2 shadow-2xl shadow-black/50 max-h-[70vh] overflow-y-auto ${menu.wide ? "grid grid-cols-2" : ""}`}>
              {menu.items.map((item) => (
                <motion.div key={item.id} variants={itemVariants}>
                  <Link
                    to={item.scroll ? "#" : `/${slug}/${item.id}`}
                    onClick={(e) => pick(e, item)}
                    data-testid={`nav-item-${item.id}`}
                    className="group flex items-start gap-3 w-full text-left px-4 py-3 rounded-xl hover:bg-white/5 transition-colors duration-200"
                  >
                    <item.icon size={17} className="mt-0.5 shrink-0 text-smipl-orange/80 group-hover:text-smipl-orange transition-colors" />
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-white">
                        {item.name}
                        <ArrowUpRight size={12} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 text-smipl-orange" />
                      </span>
                      <span className="block text-xs text-slate-500 mt-0.5 leading-snug line-clamp-2">{item.blurb}</span>
                      {item.capabilities?.length > 0 && (
                        <span className="flex flex-wrap gap-1 mt-2">
                          {item.capabilities.slice(0, 2).map((c, ci) => (
                            <span key={ci} className="rounded-md border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] text-slate-400 truncate max-w-[150px]">
                              <span className="font-mono text-smipl-orange/80 mr-1">{String(ci + 1).padStart(2, "0")}</span>
                              {c}
                            </span>
                          ))}
                        </span>
                      )}
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Navbar = ({ openContact }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [mobileGroup, setMobileGroup] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const whatsapp = () => {
    if (WA_NUMBER) window.open(`https://wa.me/${WA_NUMBER}`, "_blank");
    else openContact();
  };

  return (
    <motion.header
      initial={{ y: -90, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-4 inset-x-0 z-[100] flex justify-center px-4"
    >
      <div className="w-full max-w-7xl">
        <nav
          data-testid="main-navbar"
          className={`flex items-center justify-between gap-3 rounded-full border px-5 sm:px-7 py-3.5 backdrop-blur-xl transition-all duration-300 ${
            scrolled
              ? "bg-navy-deep/70 border-white/15 shadow-2xl shadow-black/40"
              : "bg-navy-deep/25 border-white/8 shadow-lg shadow-black/10"
          }`}
        >
          <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0 group">
            <img src={LOGO} alt="SMIPL — Swapna Meditech Industries Pvt. Ltd." className="h-14 w-auto rounded-lg object-contain transition-transform duration-300 group-hover:scale-[1.03]" />
          </Link>

          <div className="hidden lg:flex items-center">
            {NAV_MENUS.map((m) => (
              <Dropdown key={m.label} menu={m} />
            ))}
            <Link
              to="/products"
              data-testid="nav-products"
              className="group relative px-3.5 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors duration-200"
            >
              Our Products
              <span className="absolute left-3.5 right-3.5 -bottom-0.5 h-px bg-smipl-orange opacity-0 scale-x-0 group-hover:opacity-100 group-hover:scale-x-100 transition-all duration-300 origin-left" />
            </Link>
            <Link
              to="/contact"
              data-testid="nav-contact"
              className="group relative px-3.5 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors duration-200"
            >
              Contact
              <span className="absolute left-3.5 right-3.5 -bottom-0.5 h-px bg-smipl-orange opacity-0 scale-x-0 group-hover:opacity-100 group-hover:scale-x-100 transition-all duration-300 origin-left" />
            </Link>
          </div>

          <div className="hidden lg:flex items-center gap-2.5 shrink-0">
            <button
              data-testid="nav-whatsapp-btn"
              onClick={whatsapp}
              className="flex items-center gap-1.5 rounded-full bg-wa/15 border border-wa/40 text-wa px-4 py-2.5 text-sm font-semibold hover:bg-wa hover:text-navy-deep transition-all duration-250"
            >
              <MessageCircle size={15} /> WhatsApp
            </button>
            <button
              data-testid="nav-talk-btn"
              onClick={openContact}
              className="flex items-center gap-1.5 rounded-full bg-smipl-orange text-white px-5 py-2.5 text-sm font-semibold hover:bg-smipl-amber hover:glow-orange transition-all duration-250"
            >
              Let's Talk <ArrowUpRight size={15} />
            </button>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setMobile(!mobile)}
            className="lg:hidden p-2 text-white rounded-full hover:bg-white/10 transition-colors"
          >
            {mobile ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>

        <AnimatePresence>
          {mobile && (
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
              data-testid="mobile-menu"
              data-lenis-prevent
              className="lg:hidden mt-2 rounded-3xl border border-white/12 bg-[#070C1A]/95 backdrop-blur-2xl p-4 shadow-2xl shadow-black/50 max-h-[72vh] overflow-y-auto"
            >
              {NAV_MENUS.map((m) => {
                const slug = slugOf(m.label);
                return (
                  <div key={m.label} className="border-b border-white/5 last:border-0">
                    <button
                      data-testid={`mobile-nav-${slug}`}
                      onClick={() => setMobileGroup(mobileGroup === m.label ? null : m.label)}
                      className="flex w-full items-center justify-between py-3.5 text-sm font-semibold text-white"
                    >
                      {m.label}
                      <ChevronDown size={14} className={`text-smipl-orange transition-transform duration-200 ${mobileGroup === m.label ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {mobileGroup === m.label && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                          <button
                            data-testid={`mobile-nav-${slug}-overview`}
                            onClick={() => { setMobile(false); navigate(`/${slug}`); }}
                            className="flex items-center gap-3 w-full text-left pl-2 py-2.5 text-sm text-smipl-orange"
                          >
                            <ArrowUpRight size={14} /> All {m.label}
                          </button>
                          {m.items.map((item) => (
                            <button
                              key={item.id}
                              data-testid={`mobile-nav-item-${item.id}`}
                              onClick={() => {
                                setMobile(false);
                                if (item.scroll) goSection(navigate, item.scroll);
                                else navigate(`/${slug}/${item.id}`);
                              }}
                              className="flex items-center gap-3 w-full text-left pl-2 py-2.5 text-sm text-slate-400 hover:text-white"
                            >
                              <item.icon size={15} className="text-smipl-orange/70" /> {item.name}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
              <button
                data-testid="mobile-nav-products"
                onClick={() => { setMobile(false); navigate("/products"); }}
                className="flex w-full items-center justify-between py-3.5 text-sm font-semibold text-white"
              >
                Our Products <ArrowUpRight size={14} className="text-smipl-orange" />
              </button>
              <button
                data-testid="mobile-nav-contact"
                onClick={() => { setMobile(false); navigate("/contact"); }}
                className="flex w-full items-center justify-between py-3.5 text-sm font-semibold text-white"
              >
                Contact <ArrowUpRight size={14} className="text-smipl-orange" />
              </button>
              <div className="flex gap-2 pt-4">
                <button data-testid="mobile-whatsapp-btn" onClick={whatsapp} className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-wa/15 border border-wa/40 text-wa py-2.5 text-sm font-semibold">
                  <MessageCircle size={15} /> WhatsApp
                </button>
                <button data-testid="mobile-talk-btn" onClick={() => { setMobile(false); openContact(); }} className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-smipl-orange text-white py-2.5 text-sm font-semibold">
                  Let's Talk <ArrowUpRight size={15} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Navbar;
